/**
 * Success State Events - Betrieb Müller (v2)
 *
 * Events showing positive/complete status - "Kontrolle bereit" states.
 */

import type { MockControlEvent } from '@hofsync/design-system';
import { daysFromNow, monthsFromNow } from '../farm-profile';
import { ARBSCHG_12 } from '../../legal';
import { ALL_EMPLOYEES } from '../entities/employees';

/**
 * Event 5 (NOW - Folded): Pflanzenschutz Kontrollbereit (SUCCESS)
 *
 * Same structure as Bodenproben card:
 * - Two-column layout when expanded
 * - Left: affected entities, domain link
 * - Right: reasoning panel
 */
export const EVENT_PFLANZENSCHUTZ_READY: MockControlEvent = {
  id: 'evt-pflanzenschutz-ready',
  type: 'situation-resolved',
  priority: 'success',
  title: 'Pflanzenschutz: Alle Nachweise aktuell',
  subtitle: 'Kontrollbereitschaft',
  timestamp: daysFromNow(0),
  relevantDate: daysFromNow(0),
  relatedEntity: { name: 'Pflanzenschutz', href: '#pflanzenschutz' },
  categories: ['pflanzenschutz'],
  controlType: 'Pflanzenschutz-Kontrolle',
  controlReadiness: {
    status: 'ready',
    metCount: 3,
    totalCount: 3,
    label: '3 von 3 erfüllt',
  },

  // === AFFECTED OBJECTS ===
  isAggregate: true,
  hideAggregateBadge: true,
  affectedEntities: [
    {
      id: 'sachkunde-klaus',
      name: 'Klaus Müller',
      type: 'person',
      severity: 'info',
      status: 'Sachkunde gültig bis 12/2028',
      action: { label: 'Nachweis anzeigen', href: '/my-farm/employees/klaus' },
    },
    {
      id: 'sachkunde-thomas',
      name: 'Thomas Weber',
      type: 'person',
      severity: 'info',
      status: 'Sachkunde gültig bis 08/2027',
      action: { label: 'Nachweis anzeigen', href: '/my-farm/employees/thomas' },
    },
    {
      id: 'feldspritze-amazone',
      name: 'Feldspritze Amazone',
      type: 'equipment',
      severity: 'info',
      status: 'Spritzen-TÜV bis 05/2027',
      action: { label: 'Prüfbericht anzeigen', href: '/my-farm/equipment/feldspritze' },
    },
  ],
  domainLink: {
    label: 'Alle Nachweise anzeigen',
    href: '/my-farm/pflanzenschutz',
  },

  // === WHY THIS MATTERS ===
  reasoning: {
    summary: 'Pflanzenschutz erfordert gültige Sachkundenachweise und geprüfte Geräte.',
    legalBasis: [],
    factors: [
      { label: 'Sachkundenachweise', value: '2 von 2 gültig' },
      { label: 'Geräteprüfung', value: 'Bis 05/2027' },
      { label: 'Dokumentation', value: 'Vollständig' },
    ],
    confidence: 'high',
  },
  entities: [],
};

/**
 * Event 11 (THIS SEASON - Folded): Cross-Compliance Bereit (SUCCESS)
 */
export const EVENT_CC_READY: MockControlEvent = {
  id: 'evt-cc-ready',
  type: 'situation-resolved',
  priority: 'success',
  title: 'Cross-Compliance: Alle Anforderungen erfüllt',
  subtitle: 'Kontrollbereitschaft',
  timestamp: monthsFromNow(-1),
  relevantDate: monthsFromNow(6),
  relatedEntity: { name: 'Cross-Compliance', href: '#cc' },
  categories: ['cross-compliance'],
  controlType: 'Cross-Compliance',
  controlReadiness: {
    status: 'ready',
    metCount: 20,
    totalCount: 20,
    label: '20 von 20 erfüllt',
  },

  // === AFFECTED OBJECTS ===
  isAggregate: true,
  hideAggregateBadge: true,
  affectedEntities: [
    {
      id: 'gab-group',
      name: 'GAB 1-11 (Grundanforderungen)',
      type: 'compliance',
      severity: 'info',
      status: '11 von 11 erfüllt',
      action: { label: 'Details anzeigen', href: '/my-farm/compliance/gab' },
    },
    {
      id: 'gloez-group',
      name: 'GLÖZ 1-9 (Guter Zustand)',
      type: 'compliance',
      severity: 'info',
      status: '9 von 9 erfüllt',
      action: { label: 'Details anzeigen', href: '/my-farm/compliance/gloez' },
    },
  ],
  domainLink: {
    label: 'CC-Checkliste anzeigen',
    href: '/my-farm/compliance',
  },

  // === WHY THIS MATTERS ===
  reasoning: {
    summary: 'Cross-Compliance-Anforderungen sichern die Direktzahlungen.',
    legalBasis: [],
    factors: [
      { label: 'GAB erfüllt', value: '11 von 11' },
      { label: 'GLÖZ erfüllt', value: '9 von 9' },
      { label: 'Direktzahlungen', value: 'Gesichert' },
    ],
    confidence: 'high',
  },
  entities: [],
};

/**
 * Event 12 (THIS SEASON - Folded): Arbeitssicherheit Bereit (SUCCESS)
 */
export const EVENT_ARBEITSSICHERHEIT_READY: MockControlEvent = {
  id: 'evt-arbeitssicherheit-ready',
  type: 'situation-resolved',
  priority: 'success',
  title: 'Arbeitssicherheit: Alle Unterweisungen aktuell',
  subtitle: 'Arbeitssicherheit',
  timestamp: monthsFromNow(-1),
  relevantDate: monthsFromNow(11), // Next due: Jan 2027
  relatedEntity: { name: 'Arbeitssicherheit', href: '#safety' },
  categories: ['unterweisungen'],
  controlType: 'Arbeitssicherheit',
  controlReadiness: {
    status: 'ready',
    metCount: 2,
    totalCount: 2,
    label: '2 von 2 unterwiesen',
  },

  // === AFFECTED OBJECTS ===
  isAggregate: true,
  hideAggregateBadge: true,
  affectedEntities: [
    {
      id: 'person-klaus',
      name: 'Klaus Müller',
      type: 'person',
      severity: 'info',
      status: 'Unterwiesen 01/2026',
      action: { label: 'Protokoll anzeigen', href: '/my-farm/employees/klaus' },
    },
    {
      id: 'person-thomas',
      name: 'Thomas Weber',
      type: 'person',
      severity: 'info',
      status: 'Unterwiesen 01/2026',
      action: { label: 'Protokoll anzeigen', href: '/my-farm/employees/thomas' },
    },
  ],
  domainLink: {
    label: 'Alle Unterweisungen anzeigen',
    href: '/my-farm/safety',
  },

  // === WHY THIS MATTERS ===
  reasoning: {
    summary: 'Jährliche Arbeitssicherheitsunterweisungen sind gesetzlich vorgeschrieben.',
    legalBasis: [ARBSCHG_12],
    factors: [
      { label: 'Mitarbeiter unterwiesen', value: '2 von 2' },
      { label: 'Letzte Unterweisung', value: 'Januar 2026' },
      { label: 'Nächste Fälligkeit', value: 'Januar 2027' },
    ],
    confidence: 'high',
  },
  entities: ALL_EMPLOYEES,
};

/**
 * All success state events
 */
export const SUCCESS_STATE_EVENTS: MockControlEvent[] = [
  EVENT_PFLANZENSCHUTZ_READY,
  EVENT_CC_READY,
  EVENT_ARBEITSSICHERHEIT_READY,
];
